import { Link, useNavigate } from 'react-router-dom'
import React, { useState } from 'react'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { Form } from 'semantic-ui-react'
import {
    DangerText,
    LoaderBar,
    ScreenHeight,
    TitleText,
    ImageContainer,
    FlexRowBetween,
    FlexRow,
    AppDownloadButtons,
} from 'components'
import { AuthService, FirebaseService, getPostLoginLandingPath, shouldHonorReturnUrl } from 'utility'
import { resolveHomeLanding } from 'utility/home-dashboard'
import { InspectionTaskService } from 'utility/services/inspection-task'
import { TemplateService } from 'utility/services/template'
import { useAuth } from 'context/user'
import {
    AppPrimaryButton,
    CommanAuthScreenLogin,
    FormTextFormField,
    FormPasswordFormField,
} from 'elements'
import { signInWithPopup, GoogleAuthProvider, getAuth } from 'firebase/auth'
import { Images } from 'assets'
import { useTabletAndBelowMediaQuery } from 'layout'
import FacebookLogin from 'react-facebook-login'

const LoginSchema = yup.object().shape({
    username: yup
        .string()
        .trim('Username does not allowed spaces')
        .strict()
        .required('Username is required'),
    password: yup.string().required('Password is required'),
})

const navigateAfterLogin = async (navigate, result, hasTasks, hasTemplates) => {
    const dashboardId = result?.DashboardStyle?.DashboardId
    if (dashboardId === 2) {
        navigate('/app/dashboard/groups')
        return
    }

    // /app/task becomes /app/home only for a company that switched the new home dashboard on
    const target = await resolveHomeLanding(
        result,
        getPostLoginLandingPath(result, { hasTasks, hasTemplates }),
    )
    try {
        if (target === '/app/task') {
            sessionStorage.setItem('shouldRedirectIfNoTasks', '1')
        } else {
            sessionStorage.removeItem('shouldRedirectIfNoTasks')
        }
    } catch (err) {
        // ignore storage errors
    }
    navigate(target)
}

export const LoginScreen = () => {
    const isMobile = useTabletAndBelowMediaQuery()
    const navigate = useNavigate()
    const authContext = useAuth()
    const [processing, setProcessing] = useState('')
    const [error, setError] = useState('')

    const {
        control,
        handleSubmit,
        formState: { errors },
    } = useForm({
        resolver: yupResolver(LoginSchema),
    })

    const userHasTemplates = async () => {
        try {
            // Query minimal page to determine if the user has at least one template
            const response = await TemplateService.templateList({ PageNumber: 1, PageSize: 1 })
            const total = response?.TotalCount
            const itemsLength = Array.isArray(response?.Items) ? response.Items.length : 0
            return (typeof total === 'number' ? total : itemsLength) > 0
        } catch (e) {
            // If we cannot determine, be safe and guide the user to Template List
            return false
        }
    }

    const userHasTasks = async () => {
        try {
            const result = await InspectionTaskService.list({ PageNumber: 1, PageSize: 1, SearchText: '', Status: 0, UserId: '' })
            const total = result?.TotalCount
            const itemsLength = Array.isArray(result?.Items) ? result.Items.length : 0
            return (typeof total === 'number' ? total : itemsLength) > 0
        } catch (e) {
            // On error, assume no tasks to avoid trapping user on task page incorrectly
            return false
        }
    }

    const submit = async formData => {
        try {
            setError('')
            setProcessing('Processing')
            const result = await AuthService.login({
                LoginId: formData.username,
                Password: formData.password,
            })
            authContext.login(result)

            // Decide target using tasks first, then templates
            const [hasTasks, hasTemplates] = await Promise.all([
                userHasTasks(),
                userHasTemplates(),
            ])

            // Check for return URL in query params
            const params = new URLSearchParams(window.location.search)
            const returnUrl = params.get('returnUrl')

            // Respect returnUrl only when we're not forcing Public Templates
            if (returnUrl && shouldHonorReturnUrl(returnUrl, { hasTasks, hasTemplates })) {
                const decodedUrl = decodeURIComponent(returnUrl)
                try {
                    if (decodedUrl.startsWith('/app/task')) {
                        sessionStorage.setItem('shouldRedirectIfNoTasks', '1')
                    } else {
                        sessionStorage.removeItem('shouldRedirectIfNoTasks')
                    }
                } catch (err) {
                    // ignore storage errors
                }
                navigate(decodedUrl)
            } else {
                await navigateAfterLogin(navigate, result, hasTasks, hasTemplates)
            }
        } catch (error) {
            console.log(error)
            setError('Invalid username and/or password')
        } finally {
            setProcessing('')
        }
    }

    const validatedToken = async token => {
        try {
            setProcessing('processing')
            const result = await AuthService.validateSocialToken(token || '')
            AuthService.saveUser(result)
            authContext.login(result)

            // Determine if user has tasks/templates
            const [hasTasks, hasTemplates] = await Promise.all([
                userHasTasks(),
                userHasTemplates(),
            ])

            // Check for return URL in query params
            const params = new URLSearchParams(window.location.search)
            const returnUrl = params.get('returnUrl')

            if (returnUrl && shouldHonorReturnUrl(returnUrl, { hasTasks, hasTemplates })) {
                const decodedUrl = decodeURIComponent(returnUrl)
                try {
                    if (decodedUrl.startsWith('/app/task')) {
                        sessionStorage.setItem('shouldRedirectIfNoTasks', '1')
                    } else {
                        sessionStorage.removeItem('shouldRedirectIfNoTasks')
                    }
                } catch (err) {
                    // ignore storage errors
                }
                navigate(decodedUrl)
            } else {
                await navigateAfterLogin(navigate, result, hasTasks, hasTemplates)
            }
        } finally {
            setProcessing('')
        }
    }

    const onGoogleSignIn = async () => {
        try {
            const provider = new GoogleAuthProvider()
            await signInWithPopup(FirebaseService.auth, provider)
            const token = await getAuth().currentUser?.getIdToken()
            validatedToken(token)
        } catch (error) {
            // The AuthCredential type that was used.
            const credential = GoogleAuthProvider.credentialFromError(error)
            console.log(error, credential)
        }
    }

    const fbSignIn = async data => {
        try {
            console.log('🚀 ~ file: login.js:113 ~ fbSignIn ~ data:', data)
        } catch (error) {
            console.log(error)
        } finally {
            setProcessing(false)
        }
    }

    return (
        <ScreenHeight className="container">
            <CommanAuthScreenLogin isMobile={isMobile}>
                {processing && <LoaderBar content={processing} />}
                <TitleText>
                    {' '}
                    <ImageContainer src={Images.Logo} className="img" />
                    <span>Login</span>
                </TitleText>
                <Form
                    noValidate
                    onSubmit={handleSubmit(submit)}
                    className="mt-5"
                >
                    <FormTextFormField
                        control={control}
                        label="Username"
                        name="username"
                        placeholder="Username"
                        errors={errors?.username}
                        defaultValue=""
                    />
                    <FormPasswordFormField
                        control={control}
                        label="Password"
                        name="password"
                        placeholder="Password"
                        errors={errors?.password}
                        defaultValue=""
                        type="password"
                    />
                    <Link
                        to="/forgot-password"
                        className="d-flex justify-content-end"
                    >
                        <div basic className="">
                            Forgot Password?
                        </div>
                    </Link>
                    {error && <DangerText>{error}</DangerText>}
                    <FlexRowBetween>
                        <AppPrimaryButton fluid className="mt-2">
                            Login
                        </AppPrimaryButton>
                        <AppPrimaryButton
                            type="button"
                            onClick={() => navigate('/sign-up')}
                            fluid
                            basic
                            className="mt-2"
                        >
                            Sign up
                        </AppPrimaryButton>
                    </FlexRowBetween>
                    <AppPrimaryButton
                        type="button"
                        fluid
                        basic
                        className="my-2"
                        onClick={onGoogleSignIn}
                    >
                        <FlexRow className="justify-content-center align-items-center">
                            <ImageContainer
                                src="/images/login/google.png"
                                className="g-img"
                            />
                            <div className="ml-2">Continue with Google</div>
                        </FlexRow>
                    </AppPrimaryButton>
                    {false && (
                        <FacebookLogin
                            appId="847794754073877"
                            fields="name,email,picture"
                            callback={fbSignIn}
                            icon="fa-facebook"
                        />
                    )}
                    <Link
                        to="/verification"
                        className="d-flex justify-content-center"
                    >
                        <div basic className="mt-3">
                            User verification by code
                        </div>
                    </Link>
                </Form>
                <div className="mt-4">
                    <AppDownloadButtons />
                </div>
            </CommanAuthScreenLogin>
        </ScreenHeight>
    )
}
